import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-icon';
import { ToastrService } from '../../services/toastr-service';
import { ContainerGradeRepairCodeService } from '../../services/container-grade-repair-code.service';
import { ContainerGradeRepairCodeDetailView } from '../../models/container-grade-repair-code-detail-view.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { ContainerGradeRepairCodeInformationComponent } from '../../components/create-container-grade-repair-code/container-grade-repair-code-information/container-grade-repair-code-information.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
  selector: 'app-create-container-grade-repair-code',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ErrorComponent,
    NgxSkeletonLoaderModule,
    ContainerGradeRepairCodeInformationComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './create-container-grade-repair-code.component.html',
  styleUrls: ['./create-container-grade-repair-code.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateContainerGradeRepairCodeComponent implements OnInit, OnDestroy {
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  showCancelDialog = signal<boolean>(false);
  openAuditLog = false;
  repairCode: string | null = null;
  repairMode: string | null = null;
  detailData = signal<ContainerGradeRepairCodeDetailView | null>(null);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  form = this.formBuilder.group({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly service: ContainerGradeRepairCodeService,
  ) {}

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    this.repairCode = this.route.snapshot.paramMap.get('repairCode');
    this.repairMode = this.route.snapshot.paramMap.get('repairMode');
    this.addControlsToTheFormGroup();
    if (this.repairCode && this.repairMode) {
      this.isEditMode.set(true);
      await this.loadDetailData(this.repairCode, this.repairMode);
    } else {
      this.isEditMode.set(false);
    }
  }

  private async loadDetailData(repairCode: string, repairMode: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.service.getByRepairCodeAndMode(repairCode, repairMode));
      this.detailData.set(data);
      setTimeout(() => {
        this.populateFormWithData(data);
        this.form.get('repairCode')?.disable({ emitEvent: false });
        this.form.get('mode')?.disable({ emitEvent: false });
        this.form.get('description')?.disable({ emitEvent: false });
      });
    } catch {
      this.toastrService.showToastr('Failed to load repair code grade mapping data', 'error');
      this.router.navigate(['/master-data/container-grade-repair-code']);
    }
  }

  private populateFormWithData(data: ContainerGradeRepairCodeDetailView | null): void {
    if (!data) return;
    const gradesApplicable: string[] = [];
    if (data.e) gradesApplicable.push('E');
    if (data.k) gradesApplicable.push('K');
    if (data.m) gradesApplicable.push('M');
    if (data.q) gradesApplicable.push('Q');
    if (data.s) gradesApplicable.push('S');

    this.form.patchValue({
      repairCode: data.repairCode,
      mode: data.mode,
      description: data.description,
      generateGradeChange: data.generateGradeChange ? 'yes' : 'no',
      gradesApplicable,
    });
    this.form.markAsPristine();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private addControlsToTheFormGroup(): void {
    this.form.addControl('repairCode', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
    this.form.addControl('mode', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
    this.form.addControl('description', this.formBuilder.nonNullable.control<string>(''));
    this.form.addControl('generateGradeChange', this.formBuilder.control<string | null>(null));
    this.form.addControl('gradesApplicable', this.formBuilder.control<string[]>([]));
  }

  onCancel(): void {
    if (this.form.dirty) {
      this.showCancelDialog.set(true);
    } else {
      this.navigateBack();
    }
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.navigateBack();
  }

  keepEditing(): void {
    this.showCancelDialog.set(false);
  }

  private navigateBack(): void {
    this.router.navigate(['/master-data/container-grade-repair-code']);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      for (const key of Object.keys(this.form.controls)) {
        this.form.get(key)?.markAsTouched();
      }
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }
    if (this.isEditMode()) {
      this.updateMapping();
    } else {
      this.createMapping();
    }
  }

  private createMapping(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const formValue = this.form.getRawValue() as any;
    const request = {
      repairCode: formValue.repairCode,
      mode: formValue.mode,
      generateGradeChange: formValue.generateGradeChange === 'yes',
      gradesApplicable: formValue.gradesApplicable ?? [],
    };
    this.service
      .create(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.form.reset();
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr('Mapping added successfully', 'success');
          this.router.navigate(['/master-data/container-grade-repair-code']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'creating');
        },
      });
  }

  private updateMapping(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const formValue = this.form.getRawValue() as any;
    const updatePayload = {
      generateGradeChange: formValue.generateGradeChange === 'yes',
      gradesApplicable: formValue.gradesApplicable ?? [],
    };
    this.service
      .update(this.repairCode!, this.repairMode!, updatePayload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr('Mapping updated successfully', 'success');
          this.router.navigate(['/master-data/container-grade-repair-code']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'updating');
        },
      });
  }

  private handleError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);
    if (!error) {
      this.formSubmitErrors.set([`Failed to ${operation} mapping. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} mapping. Please try again.`, 'error');
      return;
    }
    if (error.status === 400) {
      this.formSubmitErrors.set(['Invalid data. Please check your inputs.']);
      this.toastrService.showToastr('Invalid data. Please check your inputs.', 'error');
    } else if (error.status === 409) {
      this.formSubmitErrors.set(['This mapping already exists.']);
      this.toastrService.showToastr('This mapping already exists.', 'error');
    } else if (error.status === 422 && error.error?.violations) {
      const errors = error.error.violations.map((v: { message: string }) => v.message);
      this.formSubmitErrors.set(errors);
      this.toastrService.showToastr('Validation errors occurred. Please check the form.', 'error');
    } else {
      this.formSubmitErrors.set([`Failed to ${operation} mapping. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} mapping. Please try again.`, 'error');
    }
  }

  onAuditLogClick(): void {
    if (!this.repairCode || !this.repairMode) {
      this.toastrService.showToastr('Repair code keys not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  readonly auditLogFetcher = (_transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.service.getAuditLogs(this.repairCode!, this.repairMode!, isSystemLog, page, limit);
}
